import React, { useState } from 'react';
import { content } from '../data';
import { Plus, Minus, ExternalLink, FileText } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const FAQItem = ({ question, answer, index }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.1 }}
      className="border-b border-slate-200 py-6"
    >
      <button
        className="flex w-full justify-between items-center text-left group"
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className="text-xl font-semibold text-slate-900 uppercase tracking-tight pr-4 group-hover:text-[#034b89] transition-colors">
          {question}
        </span>
        <span className="flex-shrink-0 bg-[#034b89] text-white p-1 rounded-full group-hover:scale-110 transition-transform">
          {isOpen ? <Minus size={20} /> : <Plus size={20} />}
        </span>
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden"
          >
            <p className="pt-4 text-slate-600 leading-relaxed text-lg">
              {answer}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

const FAQ = () => {
  return (
    <section id="faq" className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="inline-block bg-[#034b89] text-white font-semibold uppercase tracking-widest text-xs px-4 py-1.5 rounded-full mb-4">
            Praktisk Information
          </span>
          <h2 className="text-3xl md:text-5xl font-semibold text-slate-900 uppercase">Ofte stillede spørgsmål</h2>
        </motion.div>
        <div className="mt-8">
          {content.faq.map((item, index) => (
            <FAQItem key={index} {...item} index={index} />
          ))}
        </div>

        {/* Extra Info Section */}
        <div className="mt-20 grid grid-cols-1 md:grid-cols-2 gap-8">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="bg-slate-50 p-8 rounded-2xl border border-slate-100 flex flex-col items-center text-center"
          >
            <div className="w-12 h-12 bg-[#04a6df] rounded-full flex items-center justify-center text-white mb-6">
              <ExternalLink size={24} />
            </div>
            <h3 className="text-xl font-bold text-slate-900 uppercase mb-4">{content.extra.theoryTest.title}</h3>
            <a 
              href={content.extra.theoryTest.link}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#034b89] hover:bg-[#023a6b] text-white font-semibold py-3 px-8 rounded-full transition-all"
            >
              {content.extra.theoryTest.buttonText}
              <ExternalLink size={16} />
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="bg-slate-50 p-8 rounded-2xl border border-slate-100 flex flex-col items-center text-center"
          >
            <div className="w-12 h-12 bg-[#034b89] rounded-full flex items-center justify-center text-white mb-6">
              <FileText size={24} />
            </div>
            <h3 className="text-xl font-bold text-slate-900 uppercase mb-4">{content.extra.examCards.title}</h3>
            <a 
              href={content.extra.examCards.link}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 border-2 border-[#034b89] text-[#034b89] hover:bg-[#034b89] hover:text-white font-semibold py-3 px-8 rounded-full transition-all"
            >
              {content.extra.examCards.buttonText}
              <FileText size={16} />
            </a>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-16 text-center max-w-3xl mx-auto"
        >
          <p className="text-2xl font-medium text-slate-700 italic">
            "{content.extra.vision}"
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default FAQ;
